import * as React from "react";
import { useRef, useState } from "react";
import { Button, Gapped, Modal, Spinner } from "react-ui";
import { useDispatch } from "react-redux";

import { useMetrics } from "../../../../../Controls/Internals/Metrics/MetricsProviderContext";
import {
    ZERO_REPORT_MODAL_CLOSE,
    ZERO_REPORT_MODAL_ERROR,
    ZERO_REPORT_MODAL_FILL,
    ZERO_REPORT_MODAL_OPEN,
} from "../../../../../Controls/metricsAction";
import { hideModalActionCreator } from "../../FormModalAction";
import { useAsyncEffect } from "../../../../../../../Common/UseAsyncEffect";
import { clearURLQueryParam } from "../../../../../Controls/FormParts/FormApplication/FormApplication";

import { useZeroReportOptions } from "./Provider/ZeroReportProvoder";
import { ZeroReportExceptionInfo } from "./ZeroReportExceptionInfo";
import { ZeroReportingDescription } from "./ZeroReporting.styles";

export interface ZeroReportModalProps {
    reloadForm: () => void;
    customNormalize: (normalizeId: string, abortSignal?: AbortSignal) => Promise<void>;
    localValidationBeforeSend: (validationPath: string) => Promise<boolean>;
    callInitZeroReport?: boolean;
}

export function ZeroReporting(props: ZeroReportModalProps): JSX.Element | null {
    const [loading, setLoading] = useState(false);
    const [isNormalized, setIsNormalized] = useState(false);
    const [error, setError] = useState(false);
    const abortController = useRef(new AbortController());
    const track = useMetrics();
    const { validationPath, options, description } = useZeroReportOptions().getOptions();
    const dispatch = useDispatch();

    const onClose = async () => {
        track(ZERO_REPORT_MODAL_CLOSE);
        abortController.current.abort();
        dispatch(hideModalActionCreator());
    };

    useAsyncEffect(async () => {
        track(ZERO_REPORT_MODAL_OPEN);
        clearURLQueryParam("callInitZeroReport");
        if (!React.isValidElement(options) && props.callInitZeroReport) {
            await startZeroReportingNormalize();
        }
    }, []);

    const isInvalidOptions = async (): Promise<boolean> => {
        const { localValidationBeforeSend } = props;
        if (validationPath && localValidationBeforeSend) {
            return await localValidationBeforeSend(validationPath);
        }
        return false;
    };

    const startZeroReportingNormalize = async () => {
        const checkInvalid = await isInvalidOptions();
        if (checkInvalid) {
            return;
        }
        setLoading(true);
        setError(false);
        try {
            track(ZERO_REPORT_MODAL_FILL);
            abortController.current = new AbortController();
            await props.customNormalize("zeroReport", abortController.current.signal);
            setIsNormalized(true);
            if (!abortController.current.signal.aborted) {
                props.reloadForm();
            }
        } catch (e) {
            track(ZERO_REPORT_MODAL_ERROR, { text: (e as Error).stack?.toString() ?? (e as Error).toString() });
            setError(true);
            setLoading(false);
        }
    };

    const descriptionText =
        description ??
        "Нулями заполним только обязательные поля. В остальных останутся данные, их можно удалить с помощью очистки соответствующих разделов.";

    const descriptionIndent =
        typeof descriptionText === "string" && (descriptionText === "" || descriptionText === " ") ? null : (
            <div style={{ height: "2em" }}></div>
        );
    return (
        <Modal disableClose={isNormalized} ignoreBackgroundClick width={660} onClose={onClose}>
            <Modal.Header>Заполнение нулевого отчета</Modal.Header>
            <Modal.Body>
                {loading ? (
                    <Spinner style={{ margin: "0 auto", width: "100%" }} type="normal" caption="Загрузка..." />
                ) : error ? (
                    <ZeroReportExceptionInfo />
                ) : (
                    <ZeroReportingDescription>
                        {!props.callInitZeroReport && (
                            <div>
                                {descriptionText}
                                {descriptionIndent}
                            </div>
                        )}
                        {options}
                    </ZeroReportingDescription>
                )}
            </Modal.Body>
            <Modal.Footer panel={true}>
                <Gapped gap={8}>
                    <Button disabled={loading} onClick={startZeroReportingNormalize} use="primary" size="medium">
                        Заполнить
                    </Button>
                    <Button disabled={isNormalized} onClick={onClose} size="medium">
                        Отменить
                    </Button>
                </Gapped>
            </Modal.Footer>
        </Modal>
    );
}
